import { MS_PER_DAY } from '@klareospace/pharos-lib-types';
import { formatDisplayDate } from './date-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const RELATIVE_FALLBACK_THRESHOLD_DAYS = 7;

export interface RelativeTimeLabels {
  daysAgo: (count: number) => string;
  hoursAgo: (count: number) => string;
  justNow: string;
  minutesAgo: (count: number) => string;
  never: string;
}

/**
 * Format a date as a relative time string ("just now", "5 minutes ago", etc.).
 * Falls back to `formatDisplayDate` for dates older than 7 days.
 * Returns `labels.never` for null/undefined input.
 */
export function formatRelativeDate(date: Date | null | undefined, labels: RelativeTimeLabels): string {
  if (!date) return labels.never;

  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMins < 1) return labels.justNow;
  if (diffMins < 60) return labels.minutesAgo(diffMins);
  if (diffHours < 24) return labels.hoursAgo(diffHours);
  if (diffDays < RELATIVE_FALLBACK_THRESHOLD_DAYS) return labels.daysAgo(diffDays);

  return formatDisplayDate(new Date(date));
}
